import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { Edit, Plus, Trash2, Check, GitMerge, XCircle } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import {
  getCarMakes, deleteCarMake,
  getCarModels, deleteCarModel,
  getCarColors, deleteCarColor,
  getCarSuggestions, rejectCarSuggestion,
} from "../../utils/vehicleApi";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader as RLoader, TablePagination, usePagination } from "../reporting/ReportingShared";
import LookupModal from "./LookupModal";
import MergeModal from "./MergeModal";

const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "8px 16px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
};
const iconBtn = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: C.textMed, padding: "5px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const searchInput = {
  padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13,
  width: 240, outline: "none", fontFamily: "inherit",
};
const selectStyle = {
  padding: "9px 12px", fontSize: 13, borderRadius: 10, border: `1.5px solid ${C.border}`,
  fontFamily: "inherit", outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 180,
};

const TABS = [
  ["makes", "Makes"],
  ["models", "Models"],
  ["colors", "Colours"],
  ["pending", "Pending"],
];

// Global car master data + the moderation queue for customer suggestions.
//
// Deliberately NOT merchant-scoped, unlike every other lookup page in the panel: a
// Toyota is a Toyota for every brand. That is why there is no selectedMerchant guard
// here and why the route sits behind AdminGuard.
const VehicleMasterData = () => {
  const [subTab, setSubTab] = useState("makes");
  const [loading, setLoading] = useState(false);

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const [search, setSearch] = useState("");
  const [modelMakeFilter, setModelMakeFilter] = useState("");

  const [lookupModal, setLookupModal] = useState({ open: false, kind: "make", row: null, approving: false });
  const [mergeModal, setMergeModal] = useState({ open: false, suggestion: null });

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [mk, md, cl, sg] = await Promise.all([
      getCarMakes({ status: "approved" }),
      getCarModels({ status: "approved" }),
      getCarColors(),
      getCarSuggestions(),
    ]);
    setMakes(mk || []);
    setModels(md || []);
    setColors(cl || []);
    setSuggestions(sg?.items || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { setSearch(""); }, [subTab]);

  const makeNameById = useMemo(
    () => new Map(makes.map((m) => [m.id, m.name])),
    [makes]
  );

  const q = search.trim().toLowerCase();
  const filteredMakes = useMemo(
    () => makes.filter((m) => !q || m.name.toLowerCase().includes(q) || (m.arabicName || "").includes(search.trim())),
    [makes, q, search]
  );
  const filteredModels = useMemo(
    () => models
      .filter((m) => !modelMakeFilter || String(m.carMakeId) === String(modelMakeFilter))
      .filter((m) => !q || m.name.toLowerCase().includes(q)),
    [models, modelMakeFilter, q]
  );

  // Client-side paging: the whole dataset is a single small payload (59 makes / ~451
  // models), so there is nothing to gain from round-tripping the server per page.
  // usePagination resets to page 1 whenever the filtered length changes, so searching
  // can't strand you on an empty page.
  const makesPg = usePagination(filteredMakes, 20);
  const modelsPg = usePagination(filteredModels, 20);
  const colorsPg = usePagination(colors, 20);
  const suggestionsPg = usePagination(suggestions, 10);

  const handleDelete = async (kind, row) => {
    const label = kind === "make" ? "make" : kind === "model" ? "model" : "colour";
    if (!window.confirm(`Delete "${row.name}"?\n\nIf any customer vehicle uses this ${label} the server will refuse — deactivate it instead.`)) return;
    const fn = kind === "make" ? deleteCarMake : kind === "model" ? deleteCarModel : deleteCarColor;
    const result = await fn(row.id);
    if (result) { successToast(`${row.name} deleted`); loadAll(); }
  };

  const handleReject = async (s) => {
    if (!window.confirm(
      `Reject "${s.name}"?\n\nIt will never become globally visible, but the ` +
      `${s.customerCount || 0} customer(s) already using it keep seeing it and their vehicles keep working.\n\n` +
      `If this is a duplicate, use Merge instead.`
    )) return;
    const result = await rejectCarSuggestion(s.id, { type: s.type });
    if (result) { successToast(`${s.name} rejected`); loadAll(); }
  };

  const openApprove = (s) => setLookupModal({
    open: true,
    kind: s.type === "model" ? "model" : "make",
    row: s,
    approving: true,
  });

  const activeBadge = (isActive) => isActive
    ? <Badge color={C.green} bg={C.greenLight}>Active</Badge>
    : <Badge color={C.textMed} bg={C.borderLight}>Inactive</Badge>;

  const emptyRow = (cols, text) => (
    <tr><td colSpan={cols} style={{ ...tbl.td, textAlign: "center", color: C.textLight, padding: 32 }}>{text}</td></tr>
  );

  return (
    <Fragment>
      <Breadcrumb title="Vehicle Master Data" parent="Vehicles" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            {/* Tabs. The Pending count is badged so nobody has to remember to check. */}
            <div style={{ display: "flex", gap: 0, borderBottom: `2px solid ${C.borderLight}`, marginBottom: 16, overflowX: "auto" }}>
              {TABS.map(([k, l]) => (
                <div key={k} onClick={() => setSubTab(k)} style={{
                  padding: "10px 20px", cursor: "pointer", fontWeight: subTab === k ? 700 : 500,
                  color: subTab === k ? C.coral : C.textMed,
                  borderBottom: subTab === k ? `3px solid ${C.coral}` : "3px solid transparent",
                  fontSize: 13, transition: "all 0.15s", whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: 7,
                }}>
                  {l}
                  {k === "pending" && suggestions.length > 0 && (
                    <span style={{
                      background: C.orange, color: C.white, borderRadius: 10, fontSize: 11,
                      fontWeight: 700, padding: "1px 7px", lineHeight: "16px",
                    }}>{suggestions.length}</span>
                  )}
                </div>
              ))}
            </div>

            {loading ? <RLoader /> : (
              <>
                {/* ---- Makes ---- */}
                {subTab === "makes" && (
                  <>
                    <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                      <input style={searchInput} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search makes…" />
                      <div style={{ marginLeft: "auto" }}>
                        <button style={coralBtn} onClick={() => setLookupModal({ open: true, kind: "make", row: null, approving: false })}>
                          <Plus size={14} /> Add Make
                        </button>
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={tbl.table}>
                        <thead><tr>
                          <th style={tbl.th}>Name</th><th style={tbl.th}>Arabic</th>
                          <th style={tbl.th}>Models</th><th style={tbl.th}>Sort</th>
                          <th style={tbl.th}>Status</th><th style={tbl.th}></th>
                        </tr></thead>
                        <tbody>
                          {filteredMakes.length === 0 && emptyRow(6, "No makes")}
                          {makesPg.paged.map((m) => (
                            <tr key={m.id} className="rpt-row">
                              <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{m.name}</td>
                              <td style={{ ...tbl.td, direction: "rtl", textAlign: "right" }}>{m.arabicName || "-"}</td>
                              <td style={tbl.td}>{models.filter((x) => x.carMakeId === m.id).length}</td>
                              <td style={tbl.td}>{m.sortOrder}</td>
                              <td style={tbl.td}>{activeBadge(m.isActive)}</td>
                              <td style={{ ...tbl.td, textAlign: "right", whiteSpace: "nowrap" }}>
                                <button style={iconBtn} onClick={() => setLookupModal({ open: true, kind: "make", row: m, approving: false })}>
                                  <Edit size={12} /> Edit
                                </button>{" "}
                                <button style={iconBtn} onClick={() => handleDelete("make", m)}>
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <TablePagination total={makesPg.total} page={makesPg.page} limit={makesPg.limit}
                      onPageChange={makesPg.setPage} onLimitChange={makesPg.setLimit} />
                  </>
                )}

                {/* ---- Models ---- */}
                {subTab === "models" && (
                  <>
                    <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                      <input style={searchInput} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search models…" />
                      <select style={selectStyle} value={modelMakeFilter} onChange={(e) => setModelMakeFilter(e.target.value)}>
                        <option value="">All makes</option>
                        {makes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <span style={{ fontSize: 12, color: C.textLight }}>{filteredModels.length} shown</span>
                      <div style={{ marginLeft: "auto" }}>
                        <button style={coralBtn} onClick={() => setLookupModal({ open: true, kind: "model", row: null, approving: false })}>
                          <Plus size={14} /> Add Model
                        </button>
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={tbl.table}>
                        <thead><tr>
                          <th style={tbl.th}>Make</th><th style={tbl.th}>Name</th>
                          <th style={tbl.th}>Arabic</th><th style={tbl.th}>Sort</th>
                          <th style={tbl.th}>Status</th><th style={tbl.th}></th>
                        </tr></thead>
                        <tbody>
                          {filteredModels.length === 0 && emptyRow(6, "No models")}
                          {modelsPg.paged.map((m) => (
                            <tr key={m.id} className="rpt-row">
                              <td style={{ ...tbl.td, color: C.textMed }}>{m.makeName || makeNameById.get(m.carMakeId) || "-"}</td>
                              <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{m.name}</td>
                              <td style={{ ...tbl.td, direction: "rtl", textAlign: "right" }}>{m.arabicName || "-"}</td>
                              <td style={tbl.td}>{m.sortOrder}</td>
                              <td style={tbl.td}>{activeBadge(m.isActive)}</td>
                              <td style={{ ...tbl.td, textAlign: "right", whiteSpace: "nowrap" }}>
                                <button style={iconBtn} onClick={() => setLookupModal({ open: true, kind: "model", row: m, approving: false })}>
                                  <Edit size={12} /> Edit
                                </button>{" "}
                                <button style={iconBtn} onClick={() => handleDelete("model", m)}>
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <TablePagination total={modelsPg.total} page={modelsPg.page} limit={modelsPg.limit}
                      onPageChange={modelsPg.setPage} onLimitChange={modelsPg.setLimit} />
                  </>
                )}

                {/* ---- Colours ---- */}
                {subTab === "colors" && (
                  <>
                    <div style={{
                      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                      padding: "10px 12px", fontSize: 12, color: C.textMed, marginBottom: 16,
                    }}>
                      The colour list is intentionally short and closed — customers cannot add
                      their own. Colour exists so staff can pick one car out of a queue at a
                      glance, which "light metallic bluish grey" defeats. Anything unmatched is "Other".
                    </div>
                    <div style={{ display: "flex", marginBottom: 16 }}>
                      <div style={{ marginLeft: "auto" }}>
                        <button style={coralBtn} onClick={() => setLookupModal({ open: true, kind: "color", row: null, approving: false })}>
                          <Plus size={14} /> Add Colour
                        </button>
                      </div>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={tbl.table}>
                        <thead><tr>
                          <th style={tbl.th}>Swatch</th><th style={tbl.th}>Name</th>
                          <th style={tbl.th}>Arabic</th><th style={tbl.th}>Hex</th>
                          <th style={tbl.th}>Sort</th><th style={tbl.th}>Status</th><th style={tbl.th}></th>
                        </tr></thead>
                        <tbody>
                          {colors.length === 0 && emptyRow(7, "No colours")}
                          {colorsPg.paged.map((c) => (
                            <tr key={c.id} className="rpt-row">
                              <td style={tbl.td}>
                                <span style={{
                                  display: "inline-block", width: 22, height: 22, borderRadius: 6,
                                  background: c.hex || "transparent",
                                  border: `1px solid ${c.hex ? C.border : C.textLight}`,
                                  backgroundImage: c.hex ? "none" : "linear-gradient(45deg,#eee 25%,transparent 25%,transparent 75%,#eee 75%)",
                                }} />
                              </td>
                              <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{c.name}</td>
                              <td style={{ ...tbl.td, direction: "rtl", textAlign: "right" }}>{c.arabicName}</td>
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12, color: C.textMed }}>{c.hex || "-"}</td>
                              <td style={tbl.td}>{c.sortOrder}</td>
                              <td style={tbl.td}>{activeBadge(c.isActive)}</td>
                              <td style={{ ...tbl.td, textAlign: "right", whiteSpace: "nowrap" }}>
                                <button style={iconBtn} onClick={() => setLookupModal({ open: true, kind: "color", row: c, approving: false })}>
                                  <Edit size={12} /> Edit
                                </button>{" "}
                                <button style={iconBtn} onClick={() => handleDelete("color", c)}>
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <TablePagination total={colorsPg.total} page={colorsPg.page} limit={colorsPg.limit}
                      onPageChange={colorsPg.setPage} onLimitChange={colorsPg.setLimit} />
                  </>
                )}

                {/* ---- Pending: the moderation queue ---- */}
                {subTab === "pending" && (
                  <>
                    <div style={{
                      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                      padding: "10px 12px", fontSize: 12, color: C.textMed, marginBottom: 16,
                    }}>
                      Makes and models customers added because their car wasn't listed. Each is
                      already usable by whoever added it, but invisible to everyone else until
                      approved. Sorted by how many customers drive it — that count is the signal,
                      so you don't have to know whether an unfamiliar brand is real.
                      <strong> Duplicates should be merged, not rejected.</strong>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={tbl.table}>
                        <thead><tr>
                          <th style={tbl.th}>Name</th><th style={tbl.th}>Type</th>
                          <th style={tbl.th}>Make</th><th style={tbl.th}>Customers</th>
                          <th style={tbl.th}>Submitted by</th><th style={tbl.th}>First seen</th>
                          <th style={tbl.th}></th>
                        </tr></thead>
                        <tbody>
                          {suggestions.length === 0 && emptyRow(7, "Nothing awaiting review")}
                          {suggestionsPg.paged.map((s) => (
                            <tr key={`${s.type}-${s.id}`} className="rpt-row">
                              <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{s.name}</td>
                              <td style={tbl.td}>
                                {s.type === "make"
                                  ? <Badge color={C.blue} bg={C.blueLight}>Make</Badge>
                                  : <Badge color={C.purple} bg={C.purpleLight}>Model</Badge>}
                              </td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{s.makeName || "-"}</td>
                              <td style={tbl.td}>
                                {/* Zero means orphaned — the only submitter changed their car. */}
                                <span style={{ fontWeight: 700, color: s.customerCount > 0 ? C.text : C.textLight }}>
                                  {s.customerCount}
                                </span>
                              </td>
                              <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                {s.submittedBy?.name || "-"}
                                {s.submittedBy?.phoneNo && (
                                  <div style={{ color: C.textLight, fontSize: 11 }}>{s.submittedBy.phoneNo}</div>
                                )}
                              </td>
                              <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "-"}
                              </td>
                              <td style={{ ...tbl.td, textAlign: "right", whiteSpace: "nowrap" }}>
                                <button style={{ ...iconBtn, color: C.green, borderColor: `${C.green}55` }} onClick={() => openApprove(s)}>
                                  <Check size={12} /> Approve
                                </button>{" "}
                                <button style={iconBtn} onClick={() => setMergeModal({ open: true, suggestion: s })}>
                                  <GitMerge size={12} /> Merge
                                </button>{" "}
                                <button style={{ ...iconBtn, color: C.textLight }} onClick={() => handleReject(s)}>
                                  <XCircle size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <TablePagination total={suggestionsPg.total} page={suggestionsPg.page} limit={suggestionsPg.limit}
                      onPageChange={suggestionsPg.setPage} onLimitChange={suggestionsPg.setLimit} />
                  </>
                )}
              </>
            )}
          </RCard>
        </div>
      </Container>

      <LookupModal
        isOpen={lookupModal.open}
        toggle={() => setLookupModal((p) => ({ ...p, open: false }))}
        kind={lookupModal.kind}
        row={lookupModal.row}
        approving={lookupModal.approving}
        makes={makes}
        onSaved={loadAll}
      />
      <MergeModal
        isOpen={mergeModal.open}
        toggle={() => setMergeModal({ open: false, suggestion: null })}
        suggestion={mergeModal.suggestion}
        makes={makes}
        models={models}
        onMerged={loadAll}
      />
    </Fragment>
  );
};

export default VehicleMasterData;
